import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getCoach, updateCoachProfile, updateUser } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { AllTimeZones, getTimezoneTypeahead, timezoneZodSchema } from '@/lib/time-zones.js';
import { internationalPhoneRegex } from '@/lib/string-utils.js';

import { useToast } from '@/components/ui/hooks/use-toast.js';
import { comboBoxOptionsOf } from '@/components/ui/multi-select-utils.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { FormComboBox } from '@/components/forms/form-combo-box.js';
import { FormInputField } from '@/components/forms/form-input-field.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card.js';

import { FullCoach } from '@airreading/common/data';
import { Check, X } from 'lucide-react';

const editBasicInfoFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  displayName: z.string().trim().min(1, 'Display name for students is required'),
  email: z.string().email({ message: 'Invalid email format' }),
  phone: z.string().trim().min(1, 'Phone is required').regex(internationalPhoneRegex, {
    message: 'Phone number must be in international format, e.g., +1 123-456-7890',
  }),
  timeZone: timezoneZodSchema,
  secondaryEmail: z
    .string()
    .email({ message: 'Invalid email' })
    .or(z.literal(''))
    .transform((val) => (val === '' ? null : val))
    .nullable()
    .optional(),
  smsReminderOptedIn: z.boolean().optional(),
});

export type EditBasicInfoFormSchema = z.infer<typeof editBasicInfoFormSchema>;

interface BasicInfoCardProps {
  coach: FullCoach;
  userId: string;
}

export function BasicInfoCard({ coach, userId }: BasicInfoCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: updateUserMutate } = useMutation({ mutationFn: updateUser });
  const { mutateAsync: updateCoachProfileMutate } = useMutation({ mutationFn: updateCoachProfile });

  const onSubmit = async ({ formData, disableEditing }: SubmitProps<EditBasicInfoFormSchema>) => {
    const { secondaryEmail, displayName, smsReminderOptedIn, timeZone, ...userData } = formData;

    try {
      await updateUserMutate({
        userId,
        data: {
          ...userData,
          timeZone: timeZone.value,
        },
      });

      await updateCoachProfileMutate({
        userId,
        data: {
          secondaryEmail,
          displayName,
          smsReminderOptedIn,
        },
      });

      toast({ title: 'Teacher info updated' });
      disableEditing();
      queryClient.invalidateQueries(getCoach(userId));
    } catch (e: unknown) {
      toast({
        title: airServerErrorFrom(e)?.userFacingMessage || 'Failed to update teacher info',
      });
    }
  };

  return (
    <EditableInfoCard
      headerText="Basic information"
      data-testid="teacher-basic-info"
      formSchema={editBasicInfoFormSchema}
      onSubmit={onSubmit}
      defaultValues={{
        firstName: coach.firstName || undefined,
        lastName: coach.lastName || undefined,
        timeZone: {
          value: coach.timeZone || '',
          label: AllTimeZones.find((tz) => tz.value === coach.timeZone)?.label || '',
        },
        displayName: coach.displayName || undefined,
        email: coach.email || undefined,
        secondaryEmail: coach.secondaryEmail || undefined,
        phone: coach.phone || undefined,
        smsReminderOptedIn: coach.smsReminderOptedIn || undefined,
      }}
    >
      {({ form, editing }) => (
        <>
          <InfoKeyValue
            label="First name"
            value={coach.firstName}
            edit={{
              editing,
              editModeComponent: <FormInputField name="firstName" label="First Name" form={form} />,
            }}
          />
          <InfoKeyValue
            label="Last name"
            value={coach.lastName}
            edit={{
              editing,
              editModeComponent: <FormInputField name="lastName" label="Last Name" form={form} />,
            }}
          />
          <InfoKeyValue
            label="Time zone"
            value={coach.timeZone}
            edit={{
              editing,
              editModeComponent: (
                <FormComboBox
                  name="timeZone"
                  label="Time zone"
                  form={form}
                  {...comboBoxOptionsOf(getTimezoneTypeahead)}
                  triggerProps={{ className: 'w-2/3' }}
                  required
                />
              ),
            }}
          />
          <InfoKeyValue label="State" value={coach.state || '-'} data-testid="teacher-basic-info-state" />
          <InfoKeyValue
            label="Display name for students"
            value={coach.displayName || '-'}
            edit={{
              editing,
              editModeComponent: <FormInputField name="displayName" label="Display name for students" form={form} />,
            }}
          />
          <InfoKeyValue
            label="Email"
            value={coach.email}
            edit={{
              editing,
              editModeComponent: <FormInputField name="email" label="Email" form={form} />,
            }}
          />
          <InfoKeyValue label="Referral code" value={coach.referralCode || '-'} />
          <InfoKeyValue
            label="Secondary email"
            value={coach.secondaryEmail || '-'}
            edit={{
              editing,
              editModeComponent: <FormInputField name="secondaryEmail" label="Secondary Email" form={form} />,
            }}
          />
          <InfoKeyValue
            label="Phone"
            value={coach.phone}
            edit={{
              editing,
              editModeComponent: <FormInputField name="phone" label="Phone" form={form} />,
            }}
          />
          <InfoKeyValue
            label="SMS text reminder"
            value={coach.smsReminderOptedIn ? <Check /> : <X />}
            edit={{
              editing,
              editModeComponent: <FormCheckbox name="smsReminderOptedIn" label="SMS text reminder" form={form} />,
            }}
          />
        </>
      )}
    </EditableInfoCard>
  );
}
